import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function Map({ coordinates, weatherData, locationName, onMapClick, aqi, hasSelectedLocation }) {
  const mapInstance = useRef(null);
  const radarLayerRef = useRef(null);

  // Initialize map only once on component mount
  useEffect(() => {
    if (!mapInstance.current) {
      // Create map with smooth animations
      // Start with zoomed out view of USA
      mapInstance.current = L.map('map', { 
        zoomControl: true, 
        attributionControl: true,
        zoomAnimation: true,
        fadeAnimation: true,
        markerZoomAnimation: true
      }).setView([coordinates.lat, coordinates.lon], 4);
      
      // Clean, minimal basemap
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstance.current);
      
      mapInstance.current.on('click', (e) => {
        if (onMapClick) onMapClick(e.latlng.lat, e.latlng.lng, "Selected Location");
      });
    }

    // Cleanup on unmount
    return () => {
      if (mapInstance.current) {
        if (radarLayerRef.current) {
          try {
            mapInstance.current.removeLayer(radarLayerRef.current);
            radarLayerRef.current = null;
          } catch (err) {
            console.error("Error removing radar layer:", err);
          }
        }
        try {
          mapInstance.current.remove();
          mapInstance.current = null;
        } catch (err) {
          console.error("Error removing map:", err);
        }
      }
    };
  }, []);

  // Update map when data changes
  useEffect(() => {
    if (!mapInstance.current) return;

    // Zoom in when a location is selected, stay zoomed out otherwise
    const zoomLevel = hasSelectedLocation ? 10 : 4;
    mapInstance.current.setView([coordinates.lat, coordinates.lon], zoomLevel);

    // Update radar layer
    if (radarLayerRef.current) {
      try {
        mapInstance.current.removeLayer(radarLayerRef.current);
      } catch (err) {
        console.error("Error removing old radar layer:", err);
      }
    }
    
    radarLayerRef.current = L.tileLayer('https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png', { 
      format: 'image/png', 
      transparent: true, 
      opacity: 0.3
    }).addTo(mapInstance.current);

    // Remove any existing markers
    mapInstance.current.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        try {
          mapInstance.current.removeLayer(layer);
        } catch (err) {
          console.error("Error removing marker:", err);
        }
      }
    });

    // Only show marker if a location has been selected
    if (hasSelectedLocation && weatherData && weatherData.length > 0) {
      L.circleMarker([coordinates.lat, coordinates.lon], { 
        radius: 6, 
        fillColor: "#ff4444", 
        color: "#fff", 
        weight: 2, 
        opacity: 1, 
        fillOpacity: 1 
      }).addTo(mapInstance.current);
    }

    setTimeout(() => { if (mapInstance.current) mapInstance.current.invalidateSize(); }, 100);
  }, [coordinates, weatherData, locationName, onMapClick, aqi, hasSelectedLocation]);

  return <div id="map" style={{ height: '100%', width: '100%' }}></div>;
}

export default Map;
